import React, { useState, useEffect, useRef } from 'react';
import { Text } from '@chakra-ui/react';
import { useSectionInView } from './WrappedSection';

const useCountUp = (targetValue, duration = 2000, inView = false) => {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);
  const intervalRef = useRef(null);
  const [shouldReduceMotion] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  });

  useEffect(() => {
    // Reset quando cambia il targetValue o quando esce dalla view
    if (!inView) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (hasAnimated.current) {
        // Non resettare se ha già animato (triggerOnce)
        return undefined;
      }
      setCount(0);
      hasAnimated.current = false;
      return undefined;
    }

    // Se non è in view o ha già animato, non fare nulla
    if (hasAnimated.current) {
      return undefined;
    }

    if (shouldReduceMotion) {
      setCount(targetValue);
      hasAnimated.current = true;
      return undefined;
    }

    // Reset count quando inizia l'animazione
    setCount(0);
    let start = 0;
    const steps = Math.ceil(duration / 16); // Numero di step per 60fps
    const increment = targetValue / steps;
    // Determina se il valore ha decimali
    const hasDecimals = targetValue % 1 !== 0;

    intervalRef.current = setInterval(() => {
      start += increment;
      if (start >= targetValue) {
        start = targetValue;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        hasAnimated.current = true;
      }
      // Se ha decimali, mantieni la precisione, altrimenti arrotonda
      setCount(hasDecimals ? Math.round(start * 100) / 100 : Math.floor(start));
    }, 16); // ~60fps

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [targetValue, duration, shouldReduceMotion, inView]);

  return count;
};

const CountUpNumber = ({
  value,
  duration = 2000,
  format = (v) => v,
  fontSize,
  fontWeight,
  color,
  inView: inViewProp,
}) => {
  // Usa il context se disponibile, altrimenti usa la prop
  const contextInView = useSectionInView();
  const inView = inViewProp !== undefined ? inViewProp : contextInView;
  const count = useCountUp(value, duration, inView);
  return (
    <Text fontSize={fontSize} fontWeight={fontWeight} color={color}>
      {format(count)}
    </Text>
  );
};

export default CountUpNumber;
